import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, seedSelectSQL, type Schema } from "@/lib/api";
import { SchemaExplorer } from "@/components/SchemaExplorer";
import { SqlCodeEditor } from "@/components/SqlCodeEditor";
import { ErrorBox, PageHeader, WarnBox } from "@/components/ui";
import {
  appendSqlHistory,
  formatSqlCell,
  sqlHistoryStorageKey,
  type SqlHistoryItem,
} from "@/lib/sql-runner";

export function SQLEditorPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<"query" | "migration">("query");
  const [sql, setSql] = useState("SELECT 1;");
  const [name, setName] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<{
    columns?: string[];
    rows?: Record<string, unknown>[];
    row_count?: number;
    warnings?: string[];
    plan?: unknown;
    migration?: unknown;
  } | null>(null);
  const [ms, setMs] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<SqlHistoryItem[]>([]);
  const [schema, setSchema] = useState<Schema | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [selected, setSelected] = useState<{
    kind: "table" | "view";
    name: string;
  } | null>(null);

  useEffect(() => {
    if (!id) return;
    try {
      const raw = localStorage.getItem(sqlHistoryStorageKey(id));
      if (raw) setHistory(JSON.parse(raw) as SqlHistoryItem[]);
    } catch {
      /* ignore */
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setSchemaLoading(true);
      try {
        const { data } = await api.getSchema(id);
        setSchema({
          ...data,
          tables: data.tables ?? [],
          views: data.views ?? [],
        });
      } catch {
        setSchema(null);
      } finally {
        setSchemaLoading(false);
      }
    })();
  }, [id]);

  // Seed SELECT from ?relation=&kind= query params (explorer “Open in SQL”).
  useEffect(() => {
    const relation = searchParams.get("relation");
    if (!relation) return;
    const kindParam = searchParams.get("kind");
    const kind: "table" | "view" =
      kindParam === "view" ? "view" : "table";
    setSql(seedSelectSQL(relation));
    setMode("query");
    setSelected({ kind, name: relation });
    // Clear params so reloads don't fight user edits
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  function pushHistory(item: SqlHistoryItem) {
    setHistory((h) => {
      const next = appendSqlHistory(h, item);
      localStorage.setItem(sqlHistoryStorageKey(id), JSON.stringify(next));
      return next;
    });
  }

  async function run() {
    setBusy(true);
    setError(null);
    setResult(null);
    const started = performance.now();
    try {
      if (mode === "query") {
        const { data } = await api.sqlQuery(id, sql);
        const elapsed = Math.round(performance.now() - started);
        setMs(elapsed);
        setResult(data);
        pushHistory({
          at: new Date().toISOString(),
          mode,
          sql,
          ok: true,
          ms: elapsed,
        });
      } else {
        const { data } = await api.sqlMigrate(id, sql, name || undefined);
        const elapsed = Math.round(performance.now() - started);
        setMs(elapsed);
        setResult({
          migration: data.migration,
          warnings: data.warnings,
        });
        pushHistory({
          at: new Date().toISOString(),
          mode,
          sql,
          ok: true,
          ms: elapsed,
        });
        // Refresh explorer after successful migration
        try {
          const { data: sch } = await api.getSchema(id);
          setSchema({
            ...sch,
            tables: sch.tables ?? [],
            views: sch.views ?? [],
          });
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      const elapsed = Math.round(performance.now() - started);
      setMs(elapsed);
      setError(e);
      pushHistory({
        at: new Date().toISOString(),
        mode,
        sql,
        ok: false,
        ms: elapsed,
        message: e instanceof Error ? e.message : "error",
      });
    } finally {
      setBusy(false);
    }
  }

  async function planOnly() {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.sqlPlan(id, sql);
      setResult({ plan: data, warnings: data.warnings });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Editor"
        title="SQL"
        description="Query mode is read-only. Migration mode records schema and data changes. Explorer lists project tables and views."
        actions={
          <div className="inline-flex rounded-lg border border-border-standard bg-surface-container-lowest p-0.5">
            <button
              type="button"
              className={
                mode === "query"
                  ? "rounded-sm bg-primary px-3 py-1.5 text-sm font-semibold text-on-primary"
                  : "rounded-md px-3 py-1.5 text-sm font-medium text-text-muted hover:text-text-primary"
              }
              onClick={() => setMode("query")}
            >
              Query
            </button>
            <button
              type="button"
              className={
                mode === "migration"
                  ? "rounded-sm bg-primary px-3 py-1.5 text-sm font-semibold text-on-primary"
                  : "rounded-md px-3 py-1.5 text-sm font-medium text-text-muted hover:text-text-primary"
              }
              onClick={() => setMode("migration")}
            >
              Migration
            </button>
          </div>
        }
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <SchemaExplorer
          projectId={id}
          schema={schema}
          loading={schemaLoading}
          selected={selected}
          onOpenSQL={(seed, rel) => {
            setSql(seed);
            setMode("query");
            setSelected(rel);
          }}
        />

        <div className="min-w-0 flex-1">
          {schema?.schema_name ? (
            <p className="mb-2 font-mono text-[11px] text-text-muted">
              search_path ={" "}
              <span className="mono-chip !py-0" data-testid="search-path-badge">
                {schema.schema_name}
              </span>
            </p>
          ) : null}

          {mode === "migration" ? (
            <div className="mb-3 max-w-md">
              <label className="label" htmlFor="migration-name">
                Migration name (optional)
              </label>
              <input
                id="migration-name"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="create_todos"
              />
            </div>
          ) : null}

          <div className="overflow-hidden rounded-xl border border-border-standard shadow-none">
            <div className="flex items-center justify-between border-b border-code-block-border bg-code-block-header px-3 py-2">
              <span className="font-mono text-[11px] text-code-block-text">
                {mode === "query" ? "read-only query" : "migration apply"}
              </span>
              <span className="font-mono text-[11px] text-secondary">
                sql · Ctrl/⌘+Enter run
              </span>
            </div>
            <SqlCodeEditor
              value={sql}
              onChange={setSql}
              onRun={() => void run()}
              height={280}
              disabled={busy}
              aria-label={
                mode === "query" ? "SQL query editor" : "SQL migration editor"
              }
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              className="btn-primary"
              type="button"
              disabled={busy}
              onClick={() => void run()}
            >
              {busy ? "Running…" : "Run"}
            </button>
            {mode === "migration" ? (
              <button
                className="btn-secondary"
                type="button"
                disabled={busy}
                onClick={() => void planOnly()}
              >
                Plan
              </button>
            ) : null}
            {ms != null ? (
              <span className="mono-chip tabular-nums">{ms} ms</span>
            ) : null}
            <span className="text-[11px] text-text-muted">
              Shortcut: Ctrl/⌘+Enter
            </span>
          </div>

          {error ? (
            <div className="mt-4">
              <ErrorBox error={error} />
            </div>
          ) : null}

          {result?.warnings && result.warnings.length > 0 ? (
            <div className="mt-4 space-y-2">
              {result.warnings.map((w, i) => (
                <WarnBox key={i}>{w}</WarnBox>
              ))}
            </div>
          ) : null}

          {result?.rows ? (
            <div className="mt-4">
              <div className="mb-2 text-sm text-text-muted">
                {result.row_count ?? result.rows.length} row(s)
              </div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      {(result.columns ?? Object.keys(result.rows[0] ?? {})).map(
                        (c) => (
                          <th key={c}>{c}</th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <tr key={i}>
                        {(result.columns ?? Object.keys(row)).map((c) => (
                          <td key={c}>{formatSqlCell(row[c])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {result?.plan || result?.migration ? (
            <pre className="card mt-4 overflow-x-auto p-4 font-mono text-xs text-text-secondary">
              {JSON.stringify(result.plan ?? result.migration, null, 2)}
            </pre>
          ) : null}

          <div className="mt-10">
            <h2 className="mb-3 font-sans text-sm font-semibold text-text-primary">
              Local query history
            </h2>
            {history.length === 0 ? (
              <p className="text-sm text-text-muted">
                Runs on this browser are listed here. Click a statement to reload
                it.
              </p>
            ) : (
              <ul className="space-y-2">
                {history.map((h, i) => (
                  <li key={i} className="card p-3.5">
                    <div className="flex flex-wrap gap-2 text-[11px] text-text-disabled">
                      <span>{new Date(h.at).toLocaleString()}</span>
                      <span className="mono-chip !py-0">{h.mode}</span>
                      <span
                        className={
                          h.ok
                            ? "font-medium text-success"
                            : "font-medium text-error"
                        }
                      >
                        {h.ok ? "ok" : "error"}
                      </span>
                      {h.ms != null ? (
                        <span className="tabular-nums">{h.ms}ms</span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="mt-1.5 block w-full truncate text-left font-mono text-xs text-text-secondary hover:text-secondary"
                      onClick={() => {
                        setSql(h.sql);
                        setMode(h.mode === "migration" ? "migration" : "query");
                      }}
                    >
                      {h.sql}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
